import React, { useContext, useEffect, useState } from "react";
import Pagereview from "../../components/pageReview/Pagereview";
import ServiceCardDetails from "../../components/cards/serviceCardDetails/serviceCardDetails";
import { FormContext } from "../CMSRenderer/CMSRenderer";

const ServiceDetails = ({ config }) => {
  const serviceId = window?.servicepageid;
  const { currentLang } = useContext(FormContext);
  const [serviceData, setServiceData] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showBreadcrumb, setShowBreadcrumb] = useState(false);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [subTypeColumns, setSubTypeColumns] = useState(null);

  useEffect(() => {
    const fetchServiceDetails = async () => {
      if (!serviceId) return;

      try {
        const response = await fetch(
          `/api/content/services/getServicesDetails/${serviceId}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch service details");
        }

        const result = await response.json();

        if (result.code === 200 && result.data) {
          setServiceData(result.data);
        } else {
          throw new Error(result.errorMsg || "No data found");
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchServiceDetails();
  }, [serviceId]);

  useEffect(() => {
    if (serviceData) {
      setShowBreadcrumb(true);
      const tEll = mapEligibilityCriteria(serviceData, currentLang);
      setEligibilityData(tEll);

      setSubTypeColumns([
        ...new Set(tEll.flatMap((item) => item.subCategory || [])),
      ]);
    }
  }, [serviceData]);

  const breadcrumbData = [
    {
      text: serviceData
        ? currentLang === "en"
          ? config?.home_en
          : config?.home_ar
        : currentLang === "ar"
          ? config?.home_ar
          : config?.home_en,
      icon: "home",
      url: "/",
    },
    {
      text: serviceData
        ? currentLang === "en"
          ? config?.services_title_en
          : config?.services_title_ar
        : currentLang === "ar"
          ? config?.services_title_ar
          : config?.services_title_en,
      icon: "menu_book",
      url: "/services",
    },
    {
      text: serviceData
        ? currentLang === "en"
          ? serviceData?.title
          : serviceData?.titleAR
        : currentLang === "ar"
          ? serviceData?.titleAR
          : serviceData?.title,
      icon: "menu_book",
      url: `/services/serviceDetails/${serviceId}`,
    },
  ];

  const handleClick = () => {
    setIsBookmarked(!isBookmarked);
  };

  const MAIN_SUB_SEGMENTATION = {
    UAENationals: { en: "UAE Nationals", ar: "المواطنين" },
    Expats: { en: "Expatriates", ar: "الوافدين" },
    Residents: { en: "Residents", ar: "المقيمين" },
    Visitors: { en: "Visitors", ar: "الزوار" },

    Men: { en: "Men", ar: "رجال" },
    Women: { en: "Women", ar: "نساء" },
    Peopleofdetermination: {
      en: "People of determination",
      ar: "أصحاب الهمم",
    },
    Elderly: { en: "Senior citizens", ar: "كبار المواطنين" },
    Children: { en: "Children", ar: "أطفال" },

    HealthCareProviders: {
      en: "Health Care Provider",
      ar: "مقدمي الرعاية الصحية",
    },
    Companies: { en: "Companies", ar: "الشركات" },

    Hospitals: { en: "Hospitals", ar: "مستشفيات" },
    Clinic: { en: "Clinics", ar: "عيادات" },
    Pharmacies: { en: "Pharmacies", ar: "صيدليات" },
    OtherFacilities: { en: "Other Facilities", ar: "منشآت أخرى" },

    DubaiGovernmentalEntities: {
      en: "Governmental Entities",
      ar: "منشآت حكومية",
    },
    FederalGovernmentalEntities: {
      en: "Federal Governmental Entities",
      ar: "الهيئات الحكومية الاتحادية",
    },
    OtherLocalGovernmentalEntities: {
      en: "Other Local Governmental Entities",
      ar: "حكومات محلية اخرى",
    },

    alliedHealth: { en: "Allied Health", ar: "مساعد صحي" },
    complementaryAndAlternativeMedicine: {
      en: "Complementary and Alternative Medicine",
      ar: "الطب التكميلي والبديل",
    },
    dentists: { en: "Dentist", ar: "طبيب أسنان" },
    nurseAndMidwife: { en: "Nurse and Midwife", ar: "ممرض وقابلة" },
    physician: { en: "Physician", ar: "طبيب" },
  };

  const segmentationTypes = {
    "G-C": {
      en: "Individual Services",
      ar: "خدمات الأفراد",
    },
    "G-CP": {
      en: "Professionals Services",
      ar: "خدمات مهنيو الرعاية الصحية",
    },
    "G-G": {
      en: "Govt/Corporate Services",
      ar: "الخدمات الحكومية/الشركات",
    },
    "G-B": {
      en: "Govt/Corporate Services",
      ar: "الخدمات الحكومية/الشركات",
    },
    "G-F": {
      en: "Health Facilities Services",
      ar: "خدمات المنشآت",
    },
  };

  function mapEligibilityCriteria(serviceData, locale = "en") {
    const split = (value) =>
      (value || "")
        .split(";#")
        .map((item) => item.trim())
        .filter(Boolean);

    const getLabel = (code) =>
      MAIN_SUB_SEGMENTATION?.[code]?.[locale] || code || "";

    const result = [];
    const addedMainCategories = [];

    const addGroup = (mainValue, subValue) => {
      const mainCategories = split(mainValue);
      const subCategories = split(subValue).map(getLabel);

      const newMainCategories = mainCategories.filter(
        (code) => !addedMainCategories.includes(code),
      );

      newMainCategories.forEach((code) => {
        result.push({
          mainCategory: getLabel(code),
          subCategory: [...subCategories],
        });

        addedMainCategories.push(code);
      });

      if (!mainCategories.length && subCategories.length) {
        result.push({
          mainCategory: "",
          subCategory: [...subCategories],
        });
      }
    };

    addGroup(serviceData?.mainGBSegmentation, serviceData?.subGBSegmentation);
    addGroup(serviceData?.mainGCSegmentation, serviceData?.subGCSegmentation);
    addGroup(serviceData?.mainGGSegmentation, "");
    addGroup(serviceData?.mainGCPSegmentation, "");

    return result;
  }

  const yesText = currentLang === "en" ? "Yes" : "نعم";
  const noText = currentLang === "en" ? "No" : "لا";

  return (
    <>
      {showBreadcrumb && (
        <dda-breadcrumb
          design="text"
          separator="chevron_right"
          custom_class=""
          component_mode=""
          data-breadcrumbs={JSON.stringify(breadcrumbData)}
        ></dda-breadcrumb>
      )}

      <h2 className="mb-4 Medium text-primary">
        {currentLang === "en"
          ? config?.services_title_en
          : config?.services_title_ar}
      </h2>

      <div className="lg:flex gap-6">
        <div className="lg:w-3/4">
          <h5>
            {currentLang === "en" ? serviceData?.title : serviceData?.titleAR}
          </h5>
          <p
            className="body my-4 text-onSurfaceVariant"
            dangerouslySetInnerHTML={{
              __html:
                currentLang === "en"
                  ? serviceData?.serviceDescriptionEN
                  : serviceData?.serviceDescriptionAR,
            }}
          ></p>
        </div>

        <div className="lg:w-1/4">
          {serviceData?.applyNowLink && (
            <a
              href={serviceData?.applyNowLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <dda-button
                button_color="default-primary"
                start_icon=""
                button_text={
                  currentLang === "en"
                    ? config?.apply_button_en
                    : config?.apply_button_ar
                }
                end_icon="arrow_forward"
                custom_class="w-full justify-between"
                component_mode=""
                button_id="button"
                aria_label="button"
                onclick="console.log('clicked')"
                button_name="button"
                class="hydrated"
              ></dda-button>
            </a>
          )}

          <div className="grid grid-cols-2 gap-2 my-2">
            {/* <dda-button
              button_color="default-tertiary"
              start_icon=""
              button_text={
                currentLang === "en"
                  ? config?.bookmark_button_en
                  : config?.bookmark_button_ar
              }
              end_icon={isBookmarked ? "bookmark" : "bookmark_outline"}
              custom_class="w-full justify-between"
              component_mode=""
              button_id="button"
              aria_label="button"
              onclick={handleClick}
              button_name="button"
              class="hydrated"
            ></dda-button> */}
            <dda-button
              button_color="default-tertiary"
              start_icon=""
              button_text={
                currentLang === "en"
                  ? config?.share_button_en
                  : config?.share_button_ar
              }
              end_icon="share"
              custom_class="w-full justify-between"
              component_mode=""
              button_id="button"
              aria_label="button"
              onClick={() => {
                const url = window.location.href;
                navigator.clipboard.writeText(url);
                alert("Link copied to clipboard!");
              }}
              button_name="button"
              class="hydrated"
            ></dda-button>
          </div>
        </div>
      </div>

      <div className="md:flex gap-5 my-4">
        <div className="lg:w-3/4 md:w-1/2">
          <ServiceCardDetails
            imgSrc="https://stg-newdhaportal.dha.gov.ae:8090/attachments/medialib/caebda95-520e-45df-b888-daef7083fd8b-1759752245065-337251236-Customer Segmentation.svg"
            title={
              currentLang === "en"
                ? config?.customer_segmentation_en
                : config?.customer_segmentation_ar
            }
            description={
              // <div
              //   dangerouslySetInnerHTML={{
              //     __html:
              //       serviceData?.customerSegmentationType
              //         ?.split(" & ")
              //         ?.map(
              //           (eachThis) =>
              //             segmentationTypes?.[eachThis]?.[
              //               currentLang === "en" ? "en" : "ar"
              //             ] || eachThis,
              //         )
              //         ?.join(", ") || "",
              //   }}
              // />
              <ul className="list-disc mx-4">
                {serviceData?.customerSegmentationType
                  ?.split(" & ")
                  ?.map((eachThis) => (
                    <li>
                      {segmentationTypes?.[eachThis]?.[
                        currentLang === "en" ? "en" : "ar"
                      ] || eachThis}
                    </li>
                  ))}
              </ul>
            }
          />
        </div>
        <div className="lg:w-1/4 md:w-1/2 md:!my-0 my-4">
          <ServiceCardDetails
            imgSrc="/attachments/medialib/a2666c47-0db6-49d0-966a-e16007e724dd-1759752294444-749526230-Service Channel.svg"
            title={
              currentLang === "en"
                ? config?.service_channel_en
                : config?.service_channel_ar
            }
            description={
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    currentLang === "en"
                      ? serviceData?.serviceChannels.split(";#").join(", ")
                      : serviceData?.serviceChannelsAr.split(";#").join(", "),
                }}
              />
            }
          />
        </div>
      </div>

      <div className="md:flex gap-5 my-4">
        <div className="lg:w-3/4 md:w-1/2">
          <ServiceCardDetails
            imgSrc="/attachments/medialib/a8fa9b29-701e-49b3-a9ff-dc440a2b08e9-1759752306201-48442421-Service Delivery Timing.svg"
            title={
              currentLang === "en"
                ? config?.delivery_timing_en
                : config?.delivery_timing_ar
            }
            description={
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    currentLang === "en"
                      ? serviceData?.serviceDeliveryTimingEN
                      : serviceData?.serviceDeliveryTimingAR,
                }}
              />
            }
          />
        </div>
        <div className="lg:w-1/4 md:w-1/2 md:!my-0 my-4">
          <ServiceCardDetails
            imgSrc="/attachments/medialib/b14c4134-2893-4761-995a-1482d34f724a-1759752251677-327289909-Fees.svg	"
            title={
              currentLang === "en"
                ? config?.fees_title_en
                : config?.fees_title_ar
            }
            description={
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    currentLang === "en"
                      ? serviceData?.feeAmount
                      : serviceData?.feeAmountAr,
                }}
              />
            }
          />
        </div>
      </div>

      <div className="lg:flex gap-5">
        <div className="lg:w-3/4">
          <div className="dda-table-container">
            <div className="overflow-x-auto">
              <table className="dda-table w-full min-w-[650px]">
                <thead className="body">
                  <tr className="rounded-xl">
                    <th className="Bold body">
                      {currentLang === "en"
                        ? config?.fee_description_en
                        : config?.fee_description_ar}
                    </th>
                    {/* <th className="Bold body">
                      {currentLang === "en"
                        ? config?.amount_en
                        : config?.amount_ar}
                    </th> */}
                  </tr>
                </thead>
                <tbody className="body">
                  <tr>
                    <td>
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            currentLang === "en"
                              ? serviceData?.feeDescriptionEN
                              : serviceData?.feeDescriptionAR,
                        }}
                      />
                    </td>
                    {/* <td>
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            currentLang === "en"
                              ? serviceData?.feeAmount
                              : serviceData?.feeAmountAr,
                        }}
                      />
                    </td> */}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {!!eligibilityData && (
            <div className="dda-table-container mt-4">
              <div className="overflow-x-auto">
                {/* <h2 className="Bold body">
                  {currentLang === "en"
                    ? config?.eligibility_text
                    : config?.eligibility_text_ar}
                </h2> */}

                {/* <ul className="list pl-3">
                  {eligibilityData.map((item) => {
                    return (
                      <li
                        key={item.mainCategory || item.subCategory?.join("-")}
                        className="text-black"
                      >
                        <p>
                          {item.mainCategory || "-"}{" "}
                          {!!item.subCategory &&
                            item.subCategory.length > 0 &&
                            ":"}{" "}
                          {!!item.subCategory &&
                            item.subCategory.length > 0 &&
                            item.subCategory.join(", ")}
                        </p>
                      </li>
                    );
                  })}
                </ul> */}
                <table className="dda-table w-full min-w-[650px]">
                  <thead className="body">
                    <tr className="rounded-xl">
                      <th className="Bold body">
                        {currentLang === "en"
                          ? config?.eligibility_text
                          : config?.eligibility_text_ar}
                      </th>
                      {/* 
                      {subTypeColumns.map((subType) => (
                        <th className="Bold body" key={subType}>
                          {subType}
                        </th>
                      ))} */}
                    </tr>
                  </thead>

                  <tbody className="body">
                    {eligibilityData.map((item) => (
                      <tr
                        key={item.mainCategory || item.subCategory?.join("-")}
                      >
                        <td>
                          <div>
                            <p>
                              {" "}
                              {item.mainCategory || ""}{" "}
                              {!!item.mainCategory &&
                                !!item.subCategory &&
                                item.subCategory.length > 0 &&
                                ":"}{" "}
                              {!!item.subCategory &&
                                item.subCategory.length > 0 &&
                                item.subCategory.join(", ")}
                            </p>
                          </div>
                        </td>

                        {/* {subTypeColumns.map((subType) => (
                          <td key={subType}>
                            <div>
                              <p>
                                {item.subCategory?.includes(subType)
                                  ? yesText
                                  : noText}
                              </p>
                            </div>
                          </td>
                        ))} */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {serviceData?.serviceLimitationsEN && (
            <div className="dda-table-container my-4">
              <div className="overflow-x-auto">
                <table className="dda-table w-full min-w-[650px]">
                  <thead className="body">
                    <tr className="rounded-xl">
                      <th className="Bold body">
                        {currentLang === "en"
                          ? config?.service_limitations_en
                          : config?.service_limitations_ar}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="body">
                    <tr>
                      <td>
                        <div
                          dangerouslySetInnerHTML={{
                            __html:
                              currentLang === "en"
                                ? serviceData?.serviceLimitationsEN
                                : serviceData?.serviceLimitationsAR,
                          }}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="lg:w-1/4">
          <dda-accordion
            design="bg-border"
            accordion_icon_class="material-icons material-symbols-outlined"
            accordion_icon_text="description"
            accordion_header_end_icon=""
            accordion_id="required-documents"
            header_text={
              currentLang === "en"
                ? config?.required_documents_en
                : config?.required_documents_ar
            }
            custom_class=""
            component_mode=""
            default_open="true"
          >
            <div
              className="text-onSurfaceVariant"
              dangerouslySetInnerHTML={{
                __html:
                  currentLang === "en"
                    ? serviceData?.requiredDocumentEN
                    : serviceData?.requiredDocumentAR,
              }}
            />
          </dda-accordion>

          <dda-accordion
            design="bg-border"
            accordion_icon_class="material-icons material-symbols-outlined"
            accordion_icon_text="description"
            accordion_header_end_icon=""
            accordion_id="preparation-steps"
            header_text={
              currentLang === "en"
                ? config?.preparation_steps_en
                : config?.preparation_steps_ar
            }
            custom_class="my-4"
            component_mode=""
            default_open="true"
          >
            <div
              className="text-onSurfaceVariant"
              dangerouslySetInnerHTML={{
                __html:
                  currentLang === "en"
                    ? serviceData?.preparationStepsEN
                    : serviceData?.preparationStepsAR,
              }}
            />
          </dda-accordion>
        </div>
      </div>

      <div className="dda-table-container my-4">
        <div className="overflow-x-auto">
          <table className="dda-table w-full min-w-[1200px]">
            <thead className="body">
              <tr className="rounded-xl">
                <th className="Bold body" colSpan="4">
                  {currentLang === "en"
                    ? config?.support_title_en
                    : config?.support_title_ar}
                </th>
              </tr>
            </thead>
            <tbody className="body">
              <tr>
                <td>
                  <p className="flex gap-2 items-center">
                    <img
                      src="/attachments/medialib/633ea890-bcce-4983-9782-0f8e3d74d898-1759750981972-634520495-Contact Us_International.svg	"
                      width={20}
                      height={20}
                    />
                    <span
                      dangerouslySetInnerHTML={{
                        __html:
                          currentLang === "en"
                            ? config?.help_center_en
                            : config?.help_center_ar,
                      }}
                    ></span>
                  </p>
                </td>
                <td>
                  <p className="flex gap-2 items-center">
                    <img
                      src="/attachments/medialib/1d2926ff-3f03-4183-bb45-da1c69368e05-1781267839097-465171835-tollfree.svg"
                      width={20}
                      height={20}
                    />
                    <span
                      dangerouslySetInnerHTML={{
                        __html:
                          currentLang === "en"
                            ? config?.toll_free_en
                            : config?.toll_free_ar,
                      }}
                    ></span>
                  </p>
                </td>
                <td>
                  <p className="flex gap-2 items-center">
                    <img
                      src="/attachments/medialib/633ea890-bcce-4983-9782-0f8e3d74d898-1759750981972-634520495-Contact Us_International.svg	"
                      width={20}
                      height={20}
                    />
                    <span
                      dangerouslySetInnerHTML={{
                        __html:
                          currentLang === "en"
                            ? config?.outside_uae_en
                            : config?.outside_uae_ar,
                      }}
                    ></span>
                  </p>
                </td>
                <td>
                  <p className="flex gap-2 items-center">
                    <img
                      src="/attachments/medialib/c6c8b92e-f741-4415-99b8-977233065f85-1759751011784-737920332-Email.svg"
                      width={20}
                      height={20}
                    />
                    {currentLang === "en" ? config?.email_en : config?.email_ar}
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ServiceDetails;
